import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Componentes
import { Header, Sidebar } from './components';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import { 
  DashboardPage, 
  SearchPage, 
  UsersPage, 
  ApisPage, 
  SettingsPage,
  CobrancaPage,
  ReprocessPage,
  MigrationPage,
  CsvImportPage,
  BitrixTimelinePage
} from './pages';

// Hooks
import { useAuth } from './hooks/useAuth';
import { useSearch } from './hooks/useSearch';
import { useApiStatus } from './hooks/useApiStatus';

// Configurações
import { ROUTES } from './config/routes';
import { PERMISSIONS } from './config/permissions';

function App() {
  // Estado da sidebar
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1024);
  
  // Monitorar mudanças de tamanho da tela
  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Hooks
  const { user, login, logout, isAuthenticated } = useAuth();
  const { 
    searchInputs, 
    setSearchInputs, 
    searchResults, 
    setSearchResults,
    loading, 
    searchCobranca, 
    searchAllApis,
    expandedResults,
    toggleExpandedResult
  } = useSearch();
  const { apiStatus, updateApiStatus } = useApiStatus();

  // Verificar permissões
  const hasPermission = (permission) => {
    if (!user) return false;
    return PERMISSIONS[user.role]?.includes(permission) || false;
  };

  // Se não estiver autenticado, mostrar rotas públicas (login, esqueci senha, redefinir senha)
  if (!isAuthenticated) {
    return (
      <Router>
        <ToastContainer 
          position="top-right"
          theme="colored"
          autoClose={3000}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-100 flex">
        <ToastContainer 
          position="top-right"
          theme="colored"
          autoClose={3000}
        />

        {/* Sidebar */}
        <Sidebar 
          user={user} 
          onLogout={logout}
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed}
        />

        {/* Layout Principal */}
        <div 
          className="flex-1 flex flex-col transition-all duration-300"
          style={{ 
            marginLeft: isDesktop ? (isSidebarCollapsed ? 80 : 280) : 0,
            width: isDesktop ? `calc(100% - ${isSidebarCollapsed ? 80 : 280}px)` : '100%'
          }}
        >
          {/* Header */}
          <Header 
            user={user} 
            onLogout={logout} 
          />

          {/* Conteúdo Principal */}
          <main className="flex-1 p-8 overflow-auto bg-gray-50">
            <div className="max-w-7xl mx-auto">
          <Routes>
            <Route 
              path={ROUTES.DASHBOARD} 
              element={
                <DashboardPage 
                  user={user}
                  apiStatus={apiStatus}
                  onRefreshApiStatus={updateApiStatus}
                />
              } 
            />
            
            <Route 
              path={ROUTES.SEARCH} 
              element={
                <SearchPage 
                  user={user}
                  searchInputs={searchInputs}
                  setSearchInputs={setSearchInputs}
                  searchResults={searchResults}
                  setSearchResults={setSearchResults}
                  loading={loading}
                  searchCobranca={searchCobranca}
                  searchAllApis={searchAllApis}
                  expandedResults={expandedResults}
                  toggleExpandedResult={toggleExpandedResult}
                />
              } 
            />
            
            <Route 
              path={ROUTES.USERS} 
              element={
                hasPermission('manage_users') ? (
                  <UsersPage user={user} />
                ) : (
                  <Navigate to={ROUTES.SEARCH} replace />
                )
              } 
            />
            
            <Route 
              path={ROUTES.APIS} 
              element={
                hasPermission('view_apis') ? (
                  <ApisPage 
                    apiStatus={apiStatus}
                    onRefreshApiStatus={updateApiStatus}
                  />
                ) : (
                  <Navigate to={ROUTES.SEARCH} replace />
                )
              } 
            />
            
            <Route 
              path={ROUTES.SETTINGS} 
              element={
                hasPermission('manage_settings') ? (
                  <SettingsPage user={user} />
                ) : (
                  <Navigate to={ROUTES.SEARCH} replace />
                )
              } 
            />
            
            <Route 
              path={ROUTES.COBRANCA} 
              element={
                <CobrancaPage 
                  user={user}
                />
              } 
            />
            
            <Route 
              path={ROUTES.REPROCESS} 
              element={
                hasPermission('manage_reprocess') ? (
                  <ReprocessPage />
                ) : (
                  <Navigate to={ROUTES.SEARCH} replace />
                )
              } 
            />
            
            <Route 
              path={ROUTES.MIGRATION} 
              element={
                hasPermission('view_apis') ? (
                  <MigrationPage user={user} />
                ) : (
                  <Navigate to={ROUTES.SEARCH} replace />
                )
              } 
            />

            <Route 
              path={ROUTES.CSV_IMPORT} 
              element={
                hasPermission('view_apis') ? (
                  <CsvImportPage />
                ) : (
                  <Navigate to={ROUTES.SEARCH} replace />
                )
              } 
            />
            
            <Route 
              path={ROUTES.TIMELINE} 
              element={
                hasPermission('view_apis') ? (
                  <BitrixTimelinePage />
                ) : (
                  <Navigate to={ROUTES.SEARCH} replace />
                )
              } 
            />
            
            {/* Redirecionar /search para / */}
            <Route 
              path="/search" 
              element={<Navigate to="/" replace />} 
            />
            
            <Route 
              path="/" 
              element={<Navigate to={ROUTES.SEARCH} replace />} 
            />
            
            {/* Rota catch-all para 404 */}
            <Route 
              path="*" 
              element={
                <div className="flex items-center justify-center min-h-screen">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                    <p className="text-gray-600 mb-8">Página não encontrada</p>
                    <Navigate to="/" replace />
                  </div>
                </div>
              } 
            />
            </Routes>
            </div>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;